import { groupLifeService } from '@/services';
import { markClonedQuoteAsDraft } from '@/shared/utils/clone-quote-status.util';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  clearCloneIdempotencyKey,
  getCloneIdempotencyKey,
  GroupLifeClonePendingError,
  isClonePendingStatus,
} from '@/shared/utils/group-life-clone-idempotency.util';

export function useCopyGroupLifeQuote() {
  const queryClient = useQueryClient();

  return useMutation<{ policyId: string; productCode: string }, Error, string>({
    mutationKey: ['group-life', 'copy'],
    mutationFn: async (sourcePolicyId: string) => {
      const response = await groupLifeService.copyQuote(
        sourcePolicyId,
        getCloneIdempotencyKey(sourcePolicyId)
      );
      if (!response.success || !response.data) {
        // Timeout do gateway / clone em andamento: a cotação pode já existir.
        // A chave fica guardada para a próxima tentativa não duplicar.
        if (isClonePendingStatus(response.status)) {
          throw new GroupLifeClonePendingError();
        }
        throw new Error(response.error || 'Erro ao clonar cotação');
      }
      const newPolicyId = (response.data.policyId || '').trim();
      if (!newPolicyId) {
        throw new Error('Resposta inválida ao clonar cotação');
      }
      clearCloneIdempotencyKey(sourcePolicyId);
      markClonedQuoteAsDraft(newPolicyId, new Date().toISOString());
      return { policyId: newPolicyId, productCode: response.data.productCode };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
    },
    onError: error => {
      if (error instanceof GroupLifeClonePendingError) {
        queryClient.invalidateQueries({ queryKey: ['quotes'] });
      }
    },
  });
}
